import { FinanceMarks, FinanceMarkSide } from './finance.marks';
import { JobBudget } from './job.budget';
import { SupervisorEntity } from '../contact/contact';
import { Entity, EntityLink, SearchableEntity } from '../entity';
import { JobMode } from '../enums';
import { Address } from '../shared/address';
import { Attachment } from '../shared/attachment';
import { Site, SiteTiny } from '../shared/site';
import { InvoiceTiny } from './invoice.tiny';
import { JobLabel } from './job.label';
import { PriorityTiny } from './priority.tiny';
import { SystemWorkOrderStatus } from './system.workorder.status';
import { WorkFile } from './work.file';
import { WorkOrder, WorkOrderWithTechnician } from './workorder';
import { JobPolicyThresholds } from '../policies/job.policy.thresholds';
import { GanttData, GanttSaveRequest } from '../gantt/gantt.data';
import { JobSource } from './job.source';

export interface TinyWorkOrder {
    status: string;
    technician: Entity;
}

export interface JobSummary {
    _id: string;
    jobNumber: string;
    jobName: string;
    dueDate: Date;
    createdDate?: Date;
    startDate?: Date;
    completedDate?: Date;
    priority: Entity;
    address: Address;
    customer: Entity;
    priceBook?: Entity;
    priceBookId: string;
    jobType?: Entity;
    jobTypeId: string;
    status: string;
    owner?: Entity;
    workOrders?: TinyWorkOrder[];
    completed?: number;
    technician?: Entity;
    jobStartDate?: Date;
    jobDueDate?: Date;
    labels?: Entity[];
}

export interface JobListView {
    _id: string;
    jobNumber: string;
    dueDate: Date;
    createdAt?: Date;
    updatedAt?: Date;
    startDate?: Date;
    priority: Entity;
    jobType?: Entity;
    priceBook?: Entity;
    contactPhone: String;
    contactName: String;
    address: Address;
    customer: Entity;
    status: String;
    workOrderCount: number;
    completed?: number;
    owner?: Entity;
    labels?: Entity[],
    customerWO?: String,
    billingCompany?: Entity;
}

export interface JobWithWorkOrdersView extends JobListView {
    workOrders?: WorkOrderWithTechnician[];
}

export interface ChildJob {
    _id: string;
    jobNumber?: string;
    dueDate?: Date;
    note?: string;
}

export interface JobDateSort {
    asc: Date;
    desc: Date;
}

export interface JobSorting {
    dueDate: JobDateSort;
    completedDate: JobDateSort;
    priority: number;
}


export interface Job extends SearchableEntity {

    // tenantId?: string;
    mode: JobMode;
    createdBy: Entity;
    createdAt?: Date;
    updatedAt?: Date;
    imported?: boolean;
    status?: SystemWorkOrderStatus;
    source?: JobSource;

    jobNumber: string;
    startDate?: Date;
    dueDate: Date;
    completedDate?: Date;
    completedNote?: string;
    invoice?: InvoiceTiny;
    invoices?: string[];

    name?: string;
    description: string;
    customer?: Entity;
    customerPO?: string;
    customerWO?: string;
    owner?: Entity;
    fieldSupervisor?: Entity;
    pricebook?: Entity;
    billingCompany?: Entity;
    site?: SiteTiny;

    /**
     * The customer's own address and contact, held per job so neither can drift when CRM edits the
     * customer. Both halves of `SiteTiny` are used, mirroring how `site` carries its own contact.
     * `supervisor` remains legacy Manage Job's store for the same contact.
     */
    customerSite?: SiteTiny;

    attachments?: Attachment[];

    priority?: PriorityTiny;
    type?: Entity;

    supervisor?: SupervisorEntity;
    workOrders?: WorkOrder[];
    workFiles?: WorkFile[];

    buyPrice?: number;
    sellPrice?: number;

    /** Materialised money figures (WBS-10165) — sum of non-cancelled stages' totals, written only via StageMoneyUtility. Unlike buyPrice/sellPrice above (read-time DTO decoration), these are persisted. */
    totalSell?: number;
    totalBuy?: number;

    /** Non-Billable / Non-Payable marks. */
    finance?: FinanceMarks;

    quoteId?: string;
    quoteNumber?: string;

    additionalNote?: string;
    finalisedDate?: Date;
    finalisedNote?: string;
    labels?: JobLabel[];

    sorting?: JobSorting;
    customFields?: any;
    zones?: Entity[];
    integrationId?: string;
    defectCount?: number;

    connectToken?: string; // used to store the workbuddy connect token.
    recurringJob?: Entity;
    recurrenceDate?: Date;
    template?: Entity;

    budget?: JobBudget;

    opportunity?: Entity;
    quote?: Entity;

    firstDispatchDate?: Date;

    agreement?: Entity;
    policy?: Entity;
    thresholds?: JobPolicyThresholds;
    workorderDispatchTheme?: Entity;

    claimToken?: string;
    systemLabels?: Entity[];

    links?: EntityLink[];

    idempotentKey?: string;

    integrationFields?: any;
    gantt?: GanttData;
}


export enum JobCommandIds {
    GetJobAddresses = 'GetJobAddress',
    CheckDuplicateCustomerWO = 'CheckDuplicateCustomerWO',
    GetJobGanttData = "GetJobGanttData",
    SaveJobGanttData = "SaveJobGanttData",
    GetStage = 'GetStage',
    ValidateProcessing = 'ValidateProcessing',
    /** Computed Job financial recap (entity-data transport; domain query on the server). */
    GetFinancialRecap = 'GetFinancialRecap',
    /** Budget fields on the Job (not on JobAllowUpdateFields pipeline whitelist). */
    UpdateFinancialRecapBudget = 'UpdateFinancialRecapBudget',
    /** Counts what a finance mark would do, for the confirmation dialog. Read-only. */
    FinanceLockPreflight = 'FinanceLockPreflight',
    /** Marks or unmarks one finance side, cascading to every stage and line under the scope. */
    FinanceLock = 'FinanceLock',
}

export type JobCommandRequest =
    {
        commandId: JobCommandIds.GetJobAddresses,
        data: {
            id: string;
        }
    } | {
        commandId: JobCommandIds.CheckDuplicateCustomerWO,
        data: {
            excludeJobId?: string;
            customerId: string;
            customerWO: string;
        }
    } |
    {
        commandId: JobCommandIds.GetJobGanttData,
        data: {
            projectId: string;
            isPublic: boolean;
        }
    } |
    {
        commandId: JobCommandIds.SaveJobGanttData;
        data: GanttSaveRequest;
    } |
    {
        commandId: JobCommandIds.GetStage,
        data: {
            stageId: string;
        }
    } |
    {
        commandId: JobCommandIds.ValidateProcessing,
        data: {
            jobId: string;
            systemStatusName: string;
            action?: 'delete' | 'update';
        }
    } |
    {
        commandId: JobCommandIds.GetFinancialRecap,
        data: {
            jobId: string;
        }
    } |
    {
        commandId: JobCommandIds.UpdateFinancialRecapBudget,
        data: {
            jobId: string;
            budget: JobBudget;
        }
    } |
    {
        commandId: JobCommandIds.FinanceLockPreflight,
        data: JobFinanceLockRequest;
    } |
    {
        commandId: JobCommandIds.FinanceLock,
        data: JobFinanceLockRequest;
    };

/**
 * One shape for the count and the write, so the dialog cannot state the outcome of a different
 * request than the one it then sends.
 */
export interface JobFinanceLockRequest {
    /** A stage id marks that stage alone; absent marks the job and every live stage under it. */
    jobId: string;
    stageId?: string;
    side: FinanceMarkSide;
    locked: boolean;
    /** Optional reason, kept on the activity only. */
    note?: string;
}

export enum JobSystemViewVariant {
    ServicePlan = 'ServicePlan',
    ServiceLine = 'ServiceLine',
    Operations = 'Operations',
    OperationsKanban = 'OperationsKanban',
    MyJob = 'My Job',
    RecurringJob = 'RecurringJob',
}
